import React, { useEffect, useState } from 'react';
import { IMilestoneCategory, IMilestoneData } from '../api/getMilestone';
import { Input, Modal, Table, TableColumnsType, notification } from 'antd';
import { crossIcon } from './common/Icons';
import { generateLocalId } from '../store/utils';
import AntSelect from './prescriptionPad/rightPanel/workspace/padV2/AntSelect';
import {
    mileStoneCategoryName,
    milestoneCategories,
    milestoneIdNameMapping,
} from './common/constants/milestones';

const columns: TableColumnsType<any> = [
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Category</p>,
        dataIndex: 'category',
        key: 'category',
        width: '23%',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Age</p>,
        dataIndex: 'ageGroup',
        key: 'ageGroup',
        width: '15%',
    },
    {
        title: <p className="text-12 text-darwin-neutral-800 uppercase font-700">Milestone</p>,
        dataIndex: 'milestone',
        key: 'milestone',
    },
];

const AddMilestoneModal: React.FC<{
    closeModal: () => void;
    addMilestoneCallback: (milestone: IMilestoneData) => Promise<void>;
    currAddMilestoneState:
        | {
              ageGroup: {
                  age: string;
                  maxHrsFromBirth: number;
              };
          }
        | undefined;
}> = ({ addMilestoneCallback, closeModal, currAddMilestoneState }) => {
    const [milestone, setMilestone] = useState<{
        text?: string;
        category?: IMilestoneCategory;
        categoryName?: string;
    }>();

    const data = currAddMilestoneState
        ? [
              {
                  ageGroup: (
                      <p className="font-700 text-13">{currAddMilestoneState.ageGroup.age}</p>
                  ),
                  category: (
                      <AntSelect
                          options={milestoneCategories.map((val) => {
                              return {
                                  value: mileStoneCategoryName[val],
                                  id: val,
                              };
                          })}
                          getValue={(o) => o.id}
                          getLabelToCompare={(o) => o.value}
                          placeholder="Select Category"
                          className="w-full rounded-4"
                          setValue={(option) => {
                              setMilestone((prev) => ({
                                  ...prev,
                                  category: option?.id as IMilestoneCategory,
                                  categoryName: option?.id
                                      ? milestoneIdNameMapping[option?.id]
                                      : undefined,
                              }));
                          }}
                          showSearch
                          allowClear
                      />
                  ),
                  milestone: (
                      <Input.TextArea
                          rows={1}
                          onChange={(e: any) =>
                              setMilestone((prev) => ({ ...prev, text: e.target.value }))
                          }
                          placeholder="Add milestone info here..."
                          className="rounded-4"
                      />
                  ),
              },
          ]
        : [];

    const onSubmit = () => {
        if (currAddMilestoneState) {
            if (!milestone?.category && !milestone?.text) {
                notification.error({
                    message: 'Please add category and milestone',
                    duration: 3,
                });
                return;
            }

            if (!milestone?.category || !milestone.categoryName) {
                notification.error({
                    message: 'Please add category',
                    duration: 3,
                });
                return;
            }

            if (!milestone?.text) {
                notification.error({
                    message: 'Please add milestone',
                    duration: 3,
                });
                return;
            }

            addMilestoneCallback({
                name: milestone.text,
                ageGroup: currAddMilestoneState.ageGroup.age,
                categoryId: milestone.category,
                id: generateLocalId(),
                maxHrsFromBirth: currAddMilestoneState.ageGroup.maxHrsFromBirth,
                category: milestone.categoryName,
            });
        }
    };

    useEffect(() => {
        if (!currAddMilestoneState) {
            setMilestone(undefined);
        }
    }, [currAddMilestoneState]);

    return (
        <Modal
            closeIcon={() => null}
            closable={false}
            destroyOnClose
            visible={!!currAddMilestoneState}
            centered
            footer={
                <div className="flex py-8 items-center w-full justify-between">
                    <div
                        onClick={closeModal}
                        className="px-16 py-8 btn-small cursor-pointer rounded-8 bg-white border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 text-14 font-700"
                    >
                        Close
                    </div>
                    <div
                        onClick={onSubmit}
                        className="px-16 py-8 btn-small cursor-pointer rounded-8 bg-darwin-accent-bright-blue-800 text-white text-14 font-700"
                    >
                        Done
                    </div>
                </div>
            }
            bodyStyle={{
                padding: 0,
                height: 300,
                borderRadius: 4,
                overflowY: 'hidden',
                maxHeight: 'calc(100vh - 200px)',
            }}
            onCancel={() => {
                setMilestone(undefined);
                closeModal();
            }}
            width="60%"
        >
            <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                <div className="p-16 border-b border-darwin-neutral_new-200 py-12 flex items-center justify-between">
                    <p className="text-20">Add milestone</p>
                    <span onClick={closeModal} className="cursor-pointer">
                        {crossIcon('w-14 h-14')}
                    </span>
                </div>
                <div className="px-12 flex flex-col w-full py-16 max-h-420 overflow-auto">
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        sticky={true}
                        scroll={{ x: 740 }}
                        bordered={true}
                        className="overflow-auto add-milestone-table border rounded-4 overflow-auto border-darwin-neutral_new-50 hide-scrollbar"
                    />

                    <style>
                        {`
                          .add-milestone-table {
                            .ant-table-thead {
                              .ant-table-cell {
                                  background: #F4F4F4;
                              }
                            }
                          }
                        `}
                    </style>
                </div>
            </div>
        </Modal>
    );
};

export default AddMilestoneModal;
